import React from 'react';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { ToastService, useDataTable } from '@iamsoftware/react-hooks';
import { useNavigate } from 'react-router-dom';
import { AgreementService } from '../../../../../service/AgreementService';

interface DraftTabProps {
    handleView: (rowData: any) => void;
    handleSign: (item: any) => void;
    handleSignFromDraft: (rowData: any) => void;
    handleSendEmail: (item: any) => void;
    handleEdit: (rowData: any) => void;
    handleDelete: (rowData: any) => void;
    handleShowFiles: (rowData: any) => void;
    sendingEmail: boolean;
    setReload?: (reloadFn: () => void) => void;
    handleBulkSign?: (items?: any[]) => void;
}

export const DraftTab: React.FC<DraftTabProps> = ({
    handleView,
    handleSign,
    handleSignFromDraft,
    handleSendEmail,
    handleEdit,
    handleDelete,
    handleShowFiles,
    sendingEmail,
    setReload,
    handleBulkSign
}) => {
    const navigate = useNavigate();
    const [isBulkMode, setIsBulkMode] = React.useState(false);
    const draftColumns: any[] = [
        ...(isBulkMode ? [{ selectionMode: 'multiple', width: 50 }] : []),
        {
            field: 'agreementName',
            header: 'Tên tài liệu',
            width: 100,
            matchMode: 'contains',
            dataType: 'custom' as const,
            customCell(rowData: any): any {
                return (
                    <span 
                        className="underline text-primary cursor-pointer" 
                        onClick={() => handleView(rowData)} 
                        title="Click để xem chi tiết"
                    >
                        {rowData.agreementName}
                    </span>
                );
            }
        },
        { field: 'agreementNo', header: 'Mã tài liệu', width: 120, matchMode: 'contains' },
        { field: 'agreementTypeName', header: 'Loại tài liệu', width: 150, matchMode: 'contains' },
        // {
        //     field: 'statusDescription',
        //     header: 'Trạng thái',
        //     width: 120,
        //     dataType: 'custom' as const,
        //     customCell(rowData: any): any {
        //         const isUrgent = rowData.isUrgent === 'Y';
        //         const isImportant = rowData.isImportant === 'Y';
        //         return (
        //             <div className="flex align-items-center gap-2 flex-wrap">
        //                 <Tag value="Nháp" severity="secondary" />
        //                 {isUrgent && (
        //                     <span className="flex align-items-center gap-1 text-orange-500 text-sm" title="Tài liệu cần xử lý gấp">
        //                         <i className="pi pi-bolt"></i>
        //                         <span>Khẩn cấp</span>
        //                     </span>
        //                 )}
        //                 {isImportant && (
        //                     <span className="flex align-items-center gap-1 text-indigo-500 text-sm" title="Tài liệu quan trọng">
        //                         <i className="pi pi-bookmark"></i>
        //                         <span>Quan trọng</span>
        //                     </span>
        //                 )}
        //             </div>
        //         );
        //     }
        // },
        // {
        //     field: 'mainContent',
        //     header: 'File ký',
        //     width: 200,
        //     dataType: 'custom' as const,
        //     customCell(rowData: any): any {
        //         if (!rowData.mainFileName && !rowData.mainContentLocation) return <span className="text-gray-500">Chưa có file</span>;
        //         const isPdf = rowData.mainContentType === 'application/pdf';
        //         const isWord = rowData.mainContentType?.includes('word') || rowData.mainContentType?.includes('document');
        //         const iconClass = isPdf ? 'pi-file-pdf' : (isWord ? 'pi-file-word' : 'pi-file');
        //         return (
        //             <div className="flex align-items-center gap-2">
        //                 <i className={`pi ${iconClass} text-xl`}></i>
        //                 <div>
        //                     <div className="font-medium">{rowData.mainFileName}</div>
        //                 </div>
        //             </div>
        //         );
        //     }
        // },
        {
            field: 'contentCount',
            header: 'Số file',
            width: 100,
            dataType: 'custom' as const,
            customCell(rowData: any): any {
                const count = rowData.contentCount || 0;
                return (
                    <Tag
                        value={`${count} file`}
                        severity="info"
                        style={{ cursor: count > 0 ? 'pointer' : 'default' }}
                        onClick={() => count > 0 && handleShowFiles(rowData)}
                        title={count > 0 ? 'Click để xem danh sách file' : ''}
                    />
                );
            }
        },
        {
            field: 'expiryDate',
            header: 'Thời gian',
            width: 180,
            dataType: 'custom',
            customCell(rowData: any): any {
                const formatLocal = (value?: any) => {
                    if (!value) return null;
                    const d = new Date(value);
                    const dd = String(d.getDate()).padStart(2, '0');
                    const mm = String(d.getMonth() + 1).padStart(2, '0');
                    const yyyy = d.getFullYear();
                    const HH = String(d.getHours()).padStart(2, '0');
                    const MM = String(d.getMinutes()).padStart(2, '0');
                    const SS = String(d.getSeconds()).padStart(2, '0');
                    return `${dd}/${mm}/${yyyy} ${HH}:${MM}:${SS}`;
                };

                const expiryText = rowData.expiryDate ? formatLocal(rowData.expiryDate) : 'Không có thời hạn ký';
                const updatedText = rowData.lastUpdatedStamp ? formatLocal(rowData.lastUpdatedStamp) : null;

                return (
                    <div className="text-sm">
                        <div className="flex align-items-center gap-2 mb-1">
                            <i className="pi pi-hourglass" />
                            <span>{expiryText}</span>
                        </div>
                        {updatedText && (
                            <div className="flex align-items-center gap-2">
                                <i className="pi pi-clock" />
                                <span>{updatedText}</span>
                            </div>
                        )}
                    </div>
                );
            }
        },
    ];

    const { render: renderDraft, reloadLazyData: reloadDraft, selectedItems } = useDataTable({
        tableHeader: (
            <div className="flex align-items-center justify-content-between flex-wrap gap-2 w-full">
                <span>Tài liệu nháp</span>
                <div className="flex gap-2">
                    {!isBulkMode ? (
                        <Button 
                            label="Ký hàng loạt" 
                            icon="pi pi-user-edit" 
                            className="p-button-success p-button-sm" 
                            onClick={() => setIsBulkMode(true)}
                        />
                    ) : (
                        <>
                            <Button 
                                label="Xác nhận" 
                                icon="pi pi-check" 
                                className="p-button-success p-button-sm" 
                                onClick={() => {
                                    handleBulkSign?.(selectedItems);
                                    setIsBulkMode(false);
                                }}
                            />
                            <Button 
                                label="Bỏ qua" 
                                icon="pi pi-times" 
                                className="p-button-secondary p-button-sm" 
                                onClick={() => setIsBulkMode(false)}
                            />
                        </>
                    )}
                </div>
            </div>
        ),
        selectionMode: isBulkMode ? 'multiple' : undefined,
        dataKey: 'agreementId',
        indexColumnWidth: 45,
        actionColumnWidth: 140,
        columns: draftColumns,
        getList: lazyLoadEvent => {
            return AgreementService.getAgreementList(lazyLoadEvent, 'AGT_DRAFT').then(result => {
                if (result.listData && Array.isArray(result.listData)) {
                    result.listData = result.listData.map((item: any) => ({
                        ...item,
                        mainContent: item.mainFileName || item.mainContentType ? {
                            fileName: item.mainFileName,
                            contentType: item.mainContentType,
                            contentLocation: item.referenceContentLocation || item.mainContentLocation,
                            fileId: item.fileId || item.mainFileId || item.mainContentId,
                            googleFileId: item.fileId || item.mainFileId || item.mainContentId,
                            referenceFileId: item.referenceContentLocation
                        } : null
                    }));
                }
                return {
                    listData: result.listData || [],
                    totalRecords: result.totalRecords || (result.listData || []).length
                };
            });
        },
        initActions(items: Array<any>) {
            if (items) {
                items.forEach(item => {
                    item.actions = [
                        { icon: 'pi pi-eye', label: 'Xem chi tiết', command: () => handleView(item) },
                        { icon: 'pi pi-pen-to-square', title: 'Ký tại đây', className: 'p-button-info', command: () => handleSign(item) },
                        //{ icon: 'pi pi-user-edit', title: 'Bắt đầu ký', className: 'p-button-success', command: () => handleSignFromDraft(item) },
                        { icon: 'pi pi-send', title: 'Gửi email', className: 'p-button-help', command: () => { if (!sendingEmail) handleSendEmail(item); } },

                    ];

                    item.actionMenus = [
                        
                        { icon: 'pi pi-pencil', label: 'Chỉnh sửa', command: () => handleEdit(item) },
                        { icon: 'pi pi-trash', label: 'Xóa', command: () => handleDelete(item) }
                    ];
                });
            }
        },
        originalProps: { scrollHeight: 'calc(100vh - 300px)' }
    });

    React.useEffect(() => {
        if (setReload) setReload(reloadDraft);
    }, [reloadDraft, setReload]);

    return (
        <div className="tai-lieu-ky-table">
            {renderDraft()}
        </div>
    );
};
